/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../widgets/livewidget/nls/StringResources',
	'../../../../apiHelpers/SlotAPIHelper',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../api/SlotActionsProviderAPI'
], function(stringResources, SlotAPIHelper, APIFactory, SlotActionsProviderAPI) {

	return class FilterAction {

		constructor({ content, dashboardAPI, features }) {
			this.content = content;
			this.dashboard = dashboardAPI;
			this.icons = features && features['Dashboard.Icons'];
			this.content.getFeature('SlotActions').registerProvider('filterAction', this.getAPI());
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [ SlotActionsProviderAPI ]);
			}
			return this.api;
		}

		getSlotActionList(slotId, dataItemIndex = 0, options = {}) {
			const visualization = this.getVisualization();
			const slot = visualization.getSlots().getSlot(slotId);
			const dataItem = slot.getDataItemList()[dataItemIndex];
			const filterIcon = this.icons.getIcon('common-filter');

			if(slot && this._supportAction(slot, dataItemIndex, options)) {

				if (dataItemIndex === undefined) {
					dataItemIndex = 0;
				}

				this.itemContext = {
					uniqueId: dataItem.getId(),
					itemId: dataItem.getColumnId(),
					itemName: dataItem.getLabel(),
					dataType: dataItem.getDataType(),
					sourceId: visualization.getDataSource().getId()
				};

				const type = dataItem.getType(dataItemIndex);
				if (type === 'fact') {
					this.itemContext.aggregationType = dataItem.getAggregation();
				}

				const metadataColumn = dataItem.getMetadataColumn();

				if (metadataColumn && metadataColumn.isNamedSet()) {
					this.itemContext.isNamedSet = true;
				}

				let viewOptions = {
					height: 300,
					width: 212,
					slot: slot,
					mapIndex: dataItemIndex,
					logger: this.dashboard.getGlassCoreSvc('.Logger'),
					itemContext: this.itemContext,
					dashboardApi: this.dashboard,
					visualization: this.getVisualization()
				};

				return [{
					name: 'filter',
					label: stringResources.get('toolbarActionFilter'),
					icon: filterIcon.id,
					type: 'NextView',
					removeThenApplyAction: true,
					view: {
						module: 'dashboard-analytics/dialogs/CommonViewWrapper',
						state: viewOptions
					},
					actions: {
						apply: this.applyFilter.bind(this),
						edit: this.editFilter.bind(this),
						remove: this.removeFilter.bind(this)
					}
				}];
			}

			return [];
		}

		_supportAction(slot, dataItemIndex) {
			const visualization = this.getVisualization();

			if (Array.isArray(dataItemIndex) && dataItemIndex.length != 1){
				return false;
			}

			if (visualization.getDefinition().getProperty('supportsFilterAction') === false) {
				return false;
			}

			if (dataItemIndex !== undefined && SlotAPIHelper.isMultiMeasureSeriesDataItem(slot, dataItemIndex)) {
				return false;
			}

			if (dataItemIndex === undefined && (SlotAPIHelper.isMultiMeasuresSeriesSlot(slot) || SlotAPIHelper.isMultiMeasuresValueSlot(slot))) {
				return false;
			}

			const dataItem = slot.getDataItemList()[dataItemIndex];
			const metadataColumn = dataItem && dataItem.getMetadataColumn();
			if(!metadataColumn) {
				return false;
			}

			return true;
		}

		/**
		 *
		 * @param {Object} filterSpec
		 * @example
		 * filterSpec = {
		 * 		values: [{
		 * 			u: 'dataSetDemo_csv.Product_line->[Camping Equipment]',
		 * 			d: 'Camping Equipment'
		 * 		}]
		 * }
		 *
		 * filterSpec = {
		 * 		from: {
		 * 			u: 1000000,
		 * 			d: 1000000
		 * 		},
		 * 		to: {
		 * 			u: 5000000,
		 * 			d: 5000000
		 * 		}
		 * }
		 */
		applyFilter(filterSpec) {
			this.getVisualization().getLocalFilters().addFilter(this.itemContext, filterSpec);
		}

		/**
		 *
		 * @param {Object} filterSpec
		 * @example
		 * filterSpec = {
		 * 		values: [{
		 * 			u: 'dataSetDemo_csv.Product_line->[Golf Equipment]',
		 * 			d: 'Golf Equipment'
		 * 		}]
		 * }
		 *
		 * filterSpec = {
		 * 		from: {
		 * 			u: 1000000,
		 * 			d: 1000000
		 * 		},
		 * 		to: {
		 * 			u: 5000000,
		 * 			d: 5000000
		 * 		}
		 * }
		 */
		editFilter(filterSpec) {
			this.getVisualization().getLocalFilters().editFilter(this.itemContext, filterSpec);
		}

		removeFilter() {
			this.getVisualization().getLocalFilters().removeFilter(this.itemContext);
		}

		getVisualization() {
			if (!this.visualization) {
				this.visualization = this.content.getFeature('Visualization');
			}
			return this.visualization;
		}
	};
});
